import { Component, HostListener, Input } from "@angular/core";
import * as moment from "moment";
import { NotifierService } from "src/app/_services/notifier.service";
import type { SupportDashboardDownloadData } from "../support-dashboard.component";

declare let html2pdf: any;

interface ExportTableRow {
  cells: string[];
  priority?: string;
  total?: boolean;
  colSpan?: number;
}

interface PdfChartExportItem {
  key: string;
  title: string;
}

const EMPTY_DOWNLOAD_DATA: SupportDashboardDownloadData = {
  startDate: "",
  endDate: "",
  assigneeDisplay: "All Assignees",
  totalTickets: 0,
  resolutionSLA: "0.00%",
  responseSLA: "0.00%",
  avgMTTR: "0",
  volumeData: [],
  slaOverallData: [],
  slaPriorityData: [],
  slaPriorityTotal: {
    resolution_met: 0,
    resolution_breached: 0,
    resolution_percentage: {
      value: 0,
      display: "0.00%",
    },
    response_met: 0,
    response_breached: 0,
    response_percentage: {
      value: 0,
      display: "0.00%",
    },
  },
  mttrData: [],
  mttrOverall: {
    avg_resolution_time_hours: 0,
    ticket_count: 0,
  },
  creationTimeData: [],
};

@Component({
  selector: "app-support-dashboard-download",
  templateUrl: "./support-dashboard-download.component.html",
  styleUrls: ["./support-dashboard-download.component.css"],
})
export class SupportDashboardDownloadComponent {
  @Input() disabled = false;
  @Input() exportData: SupportDashboardDownloadData | null = null;
  @Input() preparePdfCharts: () => void | Promise<void> = () => undefined;
  @Input() getChartImageUrl: (chartKey: string) => string | null = () => null;

  isDownloadMenuOpen = false;
  private readonly xlsxColumnCount = 8;

  private readonly pdfChartItems: PdfChartExportItem[] = [
    { key: "volumeByPriority", title: "Volume by Priority" },
    { key: "slaComplianceOverall", title: "SLA Compliance - Overall" },
    { key: "slaComplianceByPriority", title: "SLA Compliance by Priority" },
    // { key: "mttrByPriority", title: "MTTR by Priority" },
    { key: "ticketsByCreationTime", title: "Tickets by Creation Time" },
  ];

  constructor(private notifier: NotifierService) {}

  @HostListener("document:click")
  onDocumentClick(): void {
    this.isDownloadMenuOpen = false;
  }

  toggleDownloadMenu(event: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.disabled) {
      this.isDownloadMenuOpen = false;
      return;
    }

    this.isDownloadMenuOpen = !this.isDownloadMenuOpen;
  }

  async exportPDFTrigger(event: Event): Promise<void> {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.isDownloadMenuOpen = false;
    this.notifier.loading(true);
    try {
      await this.exportPDF();
    } finally {
      this.notifier.loading(false);
    }
  }

  async exportXlsxTrigger(event: Event): Promise<void> {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.isDownloadMenuOpen = false;
    this.notifier.loading(true);
    try {
      await this.exportXlsx();
    } finally {
      this.notifier.loading(false);
    }
  }

  private get data(): SupportDashboardDownloadData {
    return this.exportData || EMPTY_DOWNLOAD_DATA;
  }

  private hasExportableData(): boolean {
    return Boolean(
      this.data.volumeData.length ||
        this.data.slaOverallData.length ||
        this.data.slaPriorityData.length ||
        this.data.mttrData.length ||
        this.data.creationTimeData.length
    );
  }

  private async exportXlsx(): Promise<void> {
    if (!this.hasExportableData()) {
      this.notifier.alert(
        "Info",
        "",
        "No data available to download.",
        "info",
        4000
      );
      return;
    }

    try {
      const ExcelJS = await import("exceljs");
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "SWAYAM";
      workbook.created = new Date();

      this.buildDashboardWorksheet(workbook);
      await this.buildChartsWorksheet(workbook);

      const buffer = await workbook.xlsx.writeBuffer();
      this.downloadWorkbook(buffer, `${this.getExportFileNameBase()}.xlsx`);
    } catch (error) {
      console.error("Support dashboard XLSX export failed", error);
      this.notifier.alert(
        "Error",
        "",
        "XLSX export is not available right now.",
        "error",
        5000
      );
    }
  }

  private async exportPDF(): Promise<void> {
    if (!this.hasExportableData()) {
      this.notifier.alert(
        "Info",
        "",
        "No data available to download.",
        "info",
        4000
      );
      return;
    }

    if (typeof html2pdf !== "function") {
      this.notifier.alert(
        "Error",
        "",
        "PDF export is not available right now.",
        "error",
        5000
      );
      return;
    }

    await this.preparePdfCharts();

    const element = document.createElement("div");
    element.innerHTML = this.buildPdfContent();

    const opt = {
      margin: 5,
      filename: `${this.getExportFileNameBase()}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { dpi: 192, scale: 2, letterRendering: true, useCORS: true },
      pagebreak: {
        mode: ["css", "legacy"],
        avoid: [
          ".pdf-section-container",
          "tr",
          "td",
          "th",
          ".pdf-section-title",
        ],
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    await html2pdf().set(opt).from(element).save();
  }

  private buildDashboardWorksheet(workbook: any): void {
    const sheet = workbook.addWorksheet("Support Dashboard", {
      views: [{ state: "frozen", ySplit: 1 }],
    });

    this.configureXlsxColumns(sheet, [24, 18, 18, 18, 18, 18, 18, 18]);

    let row = 1;
    this.mergeAndStyleRange(
      sheet,
      row,
      1,
      row,
      this.xlsxColumnCount,
      "SUPPORT ANALYTICS TICKETS DASHBOARD",
      this.getExcelPrimaryBannerStyle()
    );
    sheet.getRow(row).height = 24;
    row += 2;

    this.mergeAndStyleRange(
      sheet,
      row,
      1,
      row,
      this.xlsxColumnCount,
      "FILTERS",
      this.getExcelSectionTitleStyle()
    );
    row += 1;
    row = this.addExcelLabelValueRow(
      sheet,
      row,
      "Start Date",
      this.formatDateDisplay(this.data.startDate)
    );
    row = this.addExcelLabelValueRow(
      sheet,
      row,
      "End Date",
      this.formatDateDisplay(this.data.endDate)
    );
    row = this.addExcelLabelValueRow(
      sheet,
      row,
      "Assignees",
      this.data.assigneeDisplay
    );
    row += 1;

    this.mergeAndStyleRange(
      sheet,
      row,
      1,
      row,
      this.xlsxColumnCount,
      "SUMMARY",
      this.getExcelSectionTitleStyle()
    );
    row += 1;
    row = this.addExcelSummaryCards(sheet, row);
    row += 1;

    this.mergeAndStyleRange(
      sheet,
      row,
      1,
      row,
      this.xlsxColumnCount,
      "JIRA TICKETS DASHBOARD",
      this.getExcelPrimaryBannerStyle()
    );
    sheet.getRow(row).height = 22;
    row += 2;

    row = this.addExcelTableSection(
      sheet,
      row,
      "VOLUME BY PRIORITY",
      ["Priority", "Count", "Percentage"],
      this.getVolumeByPriorityExportRows()
    );
    row = this.addExcelTableSection(
      sheet,
      row,
      "SLA COMPLIANCE - OVERALL",
      ["SLA Type", "Met", "Breached", "Total", "Compliance %"],
      this.getSlaComplianceOverallExportRows()
    );
    row = this.addExcelTableSection(
      sheet,
      row,
      "SLA COMPLIANCE BY PRIORITY",
      [
        "Priority",
        "Resolution Met",
        "Resolution Breached",
        "Resolution %",
        "Response Met",
        "Response Breached",
        "Response %",
      ],
      this.getSlaComplianceByPriorityExportRows()
    );
    // row = this.addExcelTableSection(
    //   sheet,
    //   row,
    //   "MTTR BY PRIORITY",
    //   ["Priority", "Avg Resolution Time (Hours)", "Ticket Count"],
    //   this.getMttrByPriorityExportRows()
    // );
    row = this.addExcelTableSection(
      sheet,
      row,
      "TICKETS BY CREATION TIME",
      ["Time Period", "Ticket Count", "Percentage"],
      this.getTicketsByCreationTimeExportRows()
    );

    this.mergeAndStyleRange(
      sheet,
      row,
      1,
      row,
      this.xlsxColumnCount,
      "DASHBOARD CHARTS",
      this.getExcelSectionTitleStyle()
    );
    row += 1;
    this.mergeAndStyleRange(
      sheet,
      row,
      1,
      row,
      this.xlsxColumnCount,
      "Chart images are included in the 'Dashboard Charts' sheet.",
      this.getExcelNoteStyle()
    );
  }

  private async buildChartsWorksheet(workbook: any): Promise<void> {
    const sheet = workbook.addWorksheet("Dashboard Charts");
    this.configureXlsxColumns(sheet, [18, 18, 18, 18, 18, 18, 18, 18]);

    let row = 1;
    this.mergeAndStyleRange(
      sheet,
      row,
      1,
      row,
      this.xlsxColumnCount,
      "SUPPORT DASHBOARD CHARTS",
      this.getExcelPrimaryBannerStyle()
    );
    sheet.getRow(row).height = 24;
    row += 2;

    this.mergeAndStyleRange(
      sheet,
      row,
      1,
      row,
      this.xlsxColumnCount,
      `Tickets Between: ${this.formatDateDisplay(this.data.startDate)} to ${this.formatDateDisplay(
        this.data.endDate
      )}`,
      this.getExcelNoteStyle()
    );
    row += 1;
    this.mergeAndStyleRange(
      sheet,
      row,
      1,
      row,
      this.xlsxColumnCount,
      `Assignees: ${this.data.assigneeDisplay || "All Assignees"}`,
      this.getExcelNoteStyle()
    );
    row += 2;

    await this.preparePdfCharts();

    this.pdfChartItems.forEach((chartItem) => {
      this.mergeAndStyleRange(
        sheet,
        row,
        1,
        row,
        this.xlsxColumnCount,
        chartItem.title,
        this.getExcelSectionTitleStyle()
      );

      const imageStartRow = row + 1;
      const imageEndRow = row + 15;
      this.styleRange(
        sheet,
        imageStartRow,
        1,
        imageEndRow,
        this.xlsxColumnCount,
        this.getExcelChartContainerStyle()
      );

      for (let imageRow = imageStartRow; imageRow <= imageEndRow; imageRow += 1) {
        sheet.getRow(imageRow).height = 20;
      }

      const chartImageUrl = this.getChartImageUrl(chartItem.key);
      if (chartImageUrl) {
        const imageId = workbook.addImage({
          base64: chartImageUrl,
          extension: "png",
        });

        sheet.addImage(imageId, {
          tl: { col: 2.8, row: imageStartRow - 0.75 },
          ext: { width: 450, height: 250 },
          editAs: "absolute",
        });
      } else {
        this.mergeAndStyleRange(
          sheet,
          row + 6,
          1,
          row + 8,
          this.xlsxColumnCount,
          "Chart not available",
          this.getExcelNoteStyle()
        );
      }

      row = imageEndRow + 2;
    });
  }

  private configureXlsxColumns(sheet: any, widths: number[]): void {
    sheet.columns = widths.map((width) => ({ width }));
  }

  private addExcelLabelValueRow(
    sheet: any,
    row: number,
    label: string,
    value: string
  ): number {
    this.styleRange(sheet, row, 1, row, 1, this.getExcelLabelStyle());
    this.styleRange(
      sheet,
      row,
      2,
      row,
      this.xlsxColumnCount,
      this.getExcelValueStyle()
    );
    sheet.getCell(row, 1).value = label;
    sheet.mergeCells(row, 2, row, this.xlsxColumnCount);
    sheet.getCell(row, 2).value = value || "-";
    return row + 1;
  }

  private addExcelSummaryCards(sheet: any, row: number): number {
    const cardLabels = [
      "Total Tickets",
      "Resolution SLA",
      "Response SLA",
      // "Avg MTTR",
    ];
    const cardValues = [
      String(this.data.totalTickets),
      this.data.resolutionSLA,
      this.data.responseSLA,
      // `${this.data.avgMTTR} hrs`,
    ];

    sheet.getRow(row).height = 20;
    sheet.getRow(row + 1).height = 26;

    for (let index = 0; index < cardLabels.length; index += 1) {
      const startCol = index * 2 + 1;
      const endCol = startCol + 1;

      this.mergeAndStyleRange(
        sheet,
        row,
        startCol,
        row,
        endCol,
        cardLabels[index],
        this.getExcelSummaryLabelStyle()
      );
      this.mergeAndStyleRange(
        sheet,
        row + 1,
        startCol,
        row + 1,
        endCol,
        cardValues[index],
        this.getExcelSummaryValueStyle()
      );
    }

    return row + 3;
  }

  private addExcelTableSection(
    sheet: any,
    row: number,
    title: string,
    headers: string[],
    sectionRows: ExportTableRow[]
  ): number {
    this.mergeAndStyleRange(
      sheet,
      row,
      1,
      row,
      this.xlsxColumnCount,
      title,
      this.getExcelSectionTitleStyle()
    );
    row += 1;

    for (let index = 0; index < headers.length; index += 1) {
      const cell = sheet.getCell(row, index + 1);
      cell.value = headers[index];
      this.applyExcelStyle(cell, this.getExcelTableHeaderStyle());
    }

    row += 1;

    sectionRows.forEach((exportRow) => {
      if (exportRow.colSpan) {
        this.mergeAndStyleRange(
          sheet,
          row,
          1,
          row,
          headers.length,
          exportRow.cells[0] || "No Data Found",
          this.getExcelNoDataStyle()
        );
        row += 1;
        return;
      }

      const rowStyle = this.getExcelTableRowStyle(exportRow);
      for (let index = 0; index < headers.length; index += 1) {
        const cell = sheet.getCell(row, index + 1);
        cell.value = exportRow.cells[index] || "";
        this.applyExcelStyle(
          cell,
          index === 0
            ? {
                ...rowStyle,
                alignment: {
                  ...rowStyle.alignment,
                  horizontal: "left",
                },
              }
            : rowStyle
        );
      }
      row += 1;
    });

    return row + 1;
  }

  private mergeAndStyleRange(
    sheet: any,
    startRow: number,
    startCol: number,
    endRow: number,
    endCol: number,
    value: string,
    style: any
  ): void {
    this.styleRange(sheet, startRow, startCol, endRow, endCol, style);
    if (startRow !== endRow || startCol !== endCol) {
      sheet.mergeCells(startRow, startCol, endRow, endCol);
    }
    sheet.getCell(startRow, startCol).value = value;
    this.applyExcelStyle(sheet.getCell(startRow, startCol), style);
  }

  private styleRange(
    sheet: any,
    startRow: number,
    startCol: number,
    endRow: number,
    endCol: number,
    style: any
  ): void {
    for (let row = startRow; row <= endRow; row += 1) {
      for (let col = startCol; col <= endCol; col += 1) {
        this.applyExcelStyle(sheet.getCell(row, col), style);
      }
    }
  }

  private applyExcelStyle(cell: any, style: any): void {
    cell.font = style.font;
    cell.alignment = style.alignment;
    cell.border = style.border;
    if (style.fill) {
      cell.fill = style.fill;
    }
  }

  private downloadWorkbook(buffer: ArrayBuffer, fileName: string): void {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 0);
  }

  private getExcelPrimaryBannerStyle(): any {
    return this.createExcelStyle({
      fillColor: "1F4E79",
      fontColor: "FFFFFF",
      bold: true,
      fontSize: 14,
      horizontal: "center",
    });
  }

  private getExcelSectionTitleStyle(): any {
    return this.createExcelStyle({
      fillColor: "4472C4",
      fontColor: "FFFFFF",
      bold: true,
      fontSize: 12,
      horizontal: "center",
    });
  }

  private getExcelLabelStyle(): any {
    return this.createExcelStyle({
      fillColor: "D9E2F3",
      fontColor: "1F4E79",
      bold: true,
      horizontal: "left",
    });
  }

  private getExcelValueStyle(): any {
    return this.createExcelStyle({
      fillColor: "F8FAFF",
      horizontal: "left",
      wrapText: true,
    });
  }

  private getExcelSummaryLabelStyle(): any {
    return this.createExcelStyle({
      fillColor: "D9E2F3",
      fontColor: "1F4E79",
      bold: true,
      horizontal: "center",
    });
  }

  private getExcelSummaryValueStyle(): any {
    return this.createExcelStyle({
      fillColor: "F8FAFF",
      bold: true,
      fontSize: 13,
      horizontal: "center",
    });
  }

  private getExcelTableHeaderStyle(): any {
    return this.createExcelStyle({
      fillColor: "D9E2F3",
      bold: true,
      horizontal: "center",
      wrapText: true,
    });
  }

  private getExcelChartContainerStyle(): any {
    return this.createExcelStyle({
      fillColor: "FFFFFF",
      horizontal: "center",
    });
  }

  private getExcelNoteStyle(): any {
    return this.createExcelStyle({
      fillColor: "F8FAFF",
      horizontal: "center",
      wrapText: true,
    });
  }

  private getExcelNoDataStyle(): any {
    return this.createExcelStyle({
      horizontal: "center",
      wrapText: true,
    });
  }

  private getExcelTableRowStyle(row: ExportTableRow): any {
    if (row.total) {
      return this.createExcelStyle({
        fillColor: "C9DAF8",
        bold: true,
        horizontal: "center",
      });
    }

    const priorityColor = this.getPriorityFillColor(row.priority);
    return this.createExcelStyle({
      fillColor: priorityColor,
      horizontal: "center",
    });
  }

  private createExcelStyle({
    fillColor,
    fontColor = "000000",
    bold = false,
    fontSize = 11,
    horizontal = "left",
    wrapText = false,
  }: {
    fillColor?: string;
    fontColor?: string;
    bold?: boolean;
    fontSize?: number;
    horizontal?: "left" | "center" | "right";
    wrapText?: boolean;
  }): any {
    const style: any = {
      font: {
        name: "Calibri",
        size: fontSize,
        bold,
        color: {
          argb: this.toExcelColor(fontColor),
        },
      },
      alignment: {
        vertical: "middle",
        horizontal,
        wrapText,
      },
      border: this.getExcelBorderStyle(),
    };

    if (fillColor) {
      style.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: this.toExcelColor(fillColor),
        },
      };
    }

    return style;
  }

  private getExcelBorderStyle(): any {
    return {
      top: { style: "thin", color: { argb: "FF000000" } },
      right: { style: "thin", color: { argb: "FF000000" } },
      bottom: { style: "thin", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FF000000" } },
    };
  }

  private getPriorityFillColor(priority: string | undefined): string | undefined {
    const color = this.getPriorityRowColor(priority);
    return color ? color.replace("#", "") : undefined;
  }

  private toExcelColor(color: string): string {
    const normalizedColor = color.replace("#", "").toUpperCase();
    return normalizedColor.length === 8
      ? normalizedColor
      : `FF${normalizedColor}`;
  }

  private getVolumeByPriorityExportRows(): ExportTableRow[] {
    if (!this.data.volumeData.length) {
      return [{ cells: ["No Data Found"], colSpan: 3 }];
    }

    const rows: ExportTableRow[] = this.data.volumeData.map((row) => {
      return {
        cells: [
          row.priority || "-",
          String(row.count ?? 0),
          row.percentage?.display || "0.00%",
        ],
        priority: row.priority,
      };
    });

    rows.push({
      cells: ["Total", String(this.data.totalTickets), "100%"],
      total: true,
    });

    return rows;
  }

  private getSlaComplianceOverallExportRows(): ExportTableRow[] {
    if (!this.data.slaOverallData.length) {
      return [{ cells: ["No Data Found"], colSpan: 5 }];
    }

    return this.data.slaOverallData.map((row) => {
      return {
        cells: [
          row.sla_type || "-",
          String(row.met ?? 0),
          String(row.breached ?? 0),
          String(row.total ?? 0),
          row.compliance_percentage?.display || "0.00%",
        ],
      };
    });
  }

  private getSlaComplianceByPriorityExportRows(): ExportTableRow[] {
    if (!this.data.slaPriorityData.length) {
      return [{ cells: ["No Data Found"], colSpan: 7 }];
    }

    const rows: ExportTableRow[] = this.data.slaPriorityData.map((row) => {
      return {
        cells: [
          row.priority || "-",
          String(row.resolution_met ?? 0),
          String(row.resolution_breached ?? 0),
          row.resolution_percentage?.display || "0.00%",
          String(row.response_met ?? 0),
          String(row.response_breached ?? 0),
          row.response_percentage?.display || "0.00%",
        ],
        priority: row.priority,
      };
    });

    rows.push({
      cells: [
        "Total",
        String(this.data.slaPriorityTotal.resolution_met ?? 0),
        String(this.data.slaPriorityTotal.resolution_breached ?? 0),
        this.data.slaPriorityTotal.resolution_percentage?.display || "0.00%",
        String(this.data.slaPriorityTotal.response_met ?? 0),
        String(this.data.slaPriorityTotal.response_breached ?? 0),
        this.data.slaPriorityTotal.response_percentage?.display || "0.00%",
      ],
      total: true,
    });

    return rows;
  }

  private getMttrByPriorityExportRows(): ExportTableRow[] {
    if (!this.data.mttrData.length) {
      return [{ cells: ["No Data Found"], colSpan: 3 }];
    }

    const rows: ExportTableRow[] = this.data.mttrData.map((row) => {
      return {
        cells: [
          row.priority || "-",
          row.avg_resolution_time_hours == null
            ? "-"
            : String(row.avg_resolution_time_hours),
          String(row.ticket_count ?? 0),
        ],
        priority: row.priority,
      };
    });

    rows.push({
      cells: [
        "Overall",
        String(this.getMttrOverall().avg_resolution_time_hours ?? 0),
        String(this.getMttrOverall().ticket_count ?? 0),
      ],
      total: true,
    });

    return rows;
  }

  private getTicketsByCreationTimeExportRows(): ExportTableRow[] {
    if (!this.data.creationTimeData.length) {
      return [{ cells: ["No Data Found"], colSpan: 3 }];
    }

    return this.data.creationTimeData.map((row, index) => {
      return {
        cells: [
          row.time_period || "-",
          String(row.ticket_count ?? 0),
          row.percentage?.display || "0.00%",
        ],
        total: index === this.data.creationTimeData.length - 1,
      };
    });
  }

  private getMttrOverall() {
    return (
      this.data.mttrOverall || {
        avg_resolution_time_hours: 0,
        ticket_count: 0,
      }
    );
  }

  private formatDateDisplay(date: string): string {
    if (!date) {
      return "-";
    }

    const parsedDate = moment(date, "YYYY-MM-DD", true);
    if (!parsedDate.isValid()) {
      return date;
    }

    return parsedDate.format("DD-MMM-YYYY");
  }

  private escapeHtml(value: any): string {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  private getExportFileNameBase(): string {
    const formattedStartDate = moment(
      this.data.startDate,
      "YYYY-MM-DD",
      true
    ).isValid()
      ? moment(this.data.startDate, "YYYY-MM-DD").format("YYYYMMDD")
      : "start";
    const formattedEndDate = moment(
      this.data.endDate,
      "YYYY-MM-DD",
      true
    ).isValid()
      ? moment(this.data.endDate, "YYYY-MM-DD").format("YYYYMMDD")
      : "end";

    return `support-dashboard-${formattedStartDate}_${formattedEndDate}`;
  }

  private buildPdfContent(): string {
    const summaryItems = [
      { label: "Total Tickets", value: String(this.data.totalTickets) },
      { label: "Resolution SLA", value: this.data.resolutionSLA },
      { label: "Response SLA", value: this.data.responseSLA },
      // { label: "Avg MTTR", value: `${this.data.avgMTTR} hrs` },
    ];

    const summaryHtml = summaryItems
      .map((item) => {
        return `
          <div style="flex: 1; min-width: 140px; background-color: #D9E2F3; border: 1px solid #b8c7df; border-radius: 6px; padding: 10px; text-align: center;">
            <div style="font-size: 13px; color: #1F4E79; font-weight: 600;">${this.escapeHtml(item.label)}</div>
            <div style="font-size: 18px; font-weight: 700; margin-top: 4px;">${this.escapeHtml(item.value)}</div>
          </div>
        `;
      })
      .join("");
    const chartSectionHtml = this.buildPdfChartsSection();

    return `
      <div style="font-family: Mulish, 'Segoe UI', sans-serif; color: #111827; padding: 20px;">
        <div style="background-color: #1F4E79; padding: 10px; border-radius: 6px; text-align: center; margin-bottom: 12px;">
          <h2 style="color: #ffffff; margin: 0; font-size: 20px; font-weight: 500;">SUPPORT ANALYTICS TICKETS DASHBOARD</h2>
        </div>

        <div style="border: 1px solid #d8e1f1; border-radius: 6px; padding: 12px; margin-bottom: 14px; background-color: #f8faff;">
          <p style="margin: 0 0 4px 0; font-size: 13px;">
            <strong>Tickets Between:</strong>
          </p>
          <p style="margin: 0 0 4px 0; font-size: 13px;">
            Start Date: ${this.escapeHtml(
              this.formatDateDisplay(this.data.startDate)
            )}
          </p>
          <p style="margin: 0 0 6px 0; font-size: 13px;">
            End Date: ${this.escapeHtml(this.formatDateDisplay(this.data.endDate))}
          </p>
          <p style="margin: 0; font-size: 13px;">
            <strong>Assignees:</strong> ${this.escapeHtml(
              this.data.assigneeDisplay
            )}
          </p>
        </div>

        <div style="display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 14px;">
          ${summaryHtml}
        </div>

        <div style="background-color: #1F4E79; padding: 10px; border-radius: 6px; text-align: center; margin-bottom: 12px;">
          <h2 style="color: #ffffff; margin: 0; font-size: 20px; font-weight: 500;">JIRA TICKETS DASHBOARD</h2>
        </div>

        ${this.buildPdfSection(
          "VOLUME BY PRIORITY",
          ["Priority", "Count", "Percentage"],
          this.getVolumeByPriorityExportRows()
        )}

        ${this.buildPdfSection(
          "SLA COMPLIANCE - OVERALL",
          ["SLA Type", "Met", "Breached", "Total", "Compliance %"],
          this.getSlaComplianceOverallExportRows()
        )}

        ${this.buildPdfSection(
          "SLA COMPLIANCE BY PRIORITY",
          [
            "Priority",
            "Resolution Met",
            "Resolution Breached",
            "Resolution %",
            "Response Met",
            "Response Breached",
            "Response %",
          ],
          this.getSlaComplianceByPriorityExportRows()
        )}

        ${"" /* MTTR BY PRIORITY section intentionally hidden */}

        ${this.buildPdfSection(
          "TICKETS BY CREATION TIME",
          ["Time Period", "Ticket Count", "Percentage"],
          this.getTicketsByCreationTimeExportRows()
        )}

        ${chartSectionHtml}
      </div>
    `;
  }

  private buildPdfChartsSection(): string {
    const chartCardsHtml = this.pdfChartItems
      .map((chartItem) => {
        const chartImageUrl = this.getChartImageUrl(chartItem.key);
        if (!chartImageUrl) {
          return `
            <div
              style="
                width: calc(50% - 8px);
                min-width: 280px;
                border: 1px solid #d8e1f1;
                border-radius: 6px;
                padding: 10px;
                box-sizing: border-box;
                page-break-inside: avoid;
                break-inside: avoid;
              "
            >
              <div style="font-size: 13px; font-weight: 700; color: #1F4E79; margin-bottom: 10px;">
                ${this.escapeHtml(chartItem.title)}
              </div>
              <div
                style="
                  height: 240px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border: 1px dashed #c7d3eb;
                  color: #5c6166;
                  font-size: 12px;
                  font-weight: 600;
                  text-align: center;
                "
              >
                Chart not available
              </div>
            </div>
          `;
        }

        return `
          <div
            style="
              width: calc(50% - 8px);
              min-width: 280px;
              border: 1px solid #d8e1f1;
              border-radius: 6px;
              padding: 10px;
              box-sizing: border-box;
              page-break-inside: avoid;
              break-inside: avoid;
            "
          >
            <div style="font-size: 13px; font-weight: 700; color: #1F4E79; margin-bottom: 10px;">
              ${this.escapeHtml(chartItem.title)}
            </div>
            <img
              src="${chartImageUrl}"
              alt="${this.escapeHtml(chartItem.title)}"
              style="width: 100%; height: auto; display: block; border-radius: 4px;"
            />
          </div>
        `;
      })
      .join("");

    return `
      <div
        class="pdf-section-container"
        style="margin-bottom: 16px; page-break-inside: avoid; break-inside: avoid;"
      >
        <div class="pdf-section-title" style="background-color: #4472C4; color: #ffffff; font-weight: 700; padding: 6px 10px; margin-bottom: 0;">
          DASHBOARD CHARTS
        </div>
        <div
          style="
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            padding: 14px 0 0;
            page-break-inside: avoid;
            break-inside: avoid;
          "
        >
          ${chartCardsHtml}
        </div>
      </div>
    `;
  }

  private buildPdfSection(
    sectionTitle: string,
    headers: string[],
    rows: ExportTableRow[]
  ): string {
    const tableHeaders = headers
      .map((headerLabel) => {
        return `<th style="border: 1px solid #000; padding: 6px; background-color: #D9E2F3; text-align: center; font-size: 12px;">${this.escapeHtml(
          headerLabel
        )}</th>`;
      })
      .join("");

    const tableRows = rows
      .map((row) => {
        if (row.colSpan) {
          return `
            <tr>
              <td colspan="${headers.length}" style="border: 1px solid #000; padding: 6px; text-align: center; font-size: 12px; font-weight: 500;">
                ${this.escapeHtml(row.cells[0] || "No Data Found")}
              </td>
            </tr>
          `;
        }

        const rowBackgroundColor = row.total
          ? "#C9DAF8"
          : this.getPriorityRowColor(row.priority);
        const rowStyle = rowBackgroundColor
          ? `background-color: ${rowBackgroundColor};`
          : "";
        const cellFontWeight = row.total ? "700" : "400";
        const cells = headers
          .map((_, index) => {
            return `<td style="border: 1px solid #000; padding: 6px; font-size: 12px; font-weight: ${cellFontWeight};">${this.escapeHtml(
              row.cells[index] || ""
            )}</td>`;
          })
          .join("");

        return `<tr style="${rowStyle}">${cells}</tr>`;
      })
      .join("");

    return `
      <div
        class="pdf-section-container"
        style="margin-bottom: 16px; page-break-inside: avoid; break-inside: avoid;"
      >
        <div class="pdf-section-title" style="background-color: #4472C4; color: #ffffff; font-weight: 700; padding: 6px 10px; margin-bottom: 0;">
          ${this.escapeHtml(sectionTitle)}
        </div>
        <table style="width: 100%; border-collapse: collapse; font-size: 12px; page-break-inside: avoid; break-inside: avoid;">
          <thead>
            <tr>${tableHeaders}</tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </div>
    `;
  }

  private getPriorityRowColor(priority: string | undefined): string {
    const normalizedPriority = (priority || "").toLowerCase();
    if (normalizedPriority === "critical") {
      return "#E6B8B7";
    }
    if (normalizedPriority === "high") {
      return "#F9CB9C";
    }
    if (normalizedPriority === "medium") {
      return "#FFE599";
    }
    if (normalizedPriority === "low") {
      return "#B6D7A8";
    }

    return "";
  }
}
